// ╔══════════════════════════════════════════════════════════════╗
// ║  Settings page — production/payroll/attendance/company        ║
// ╚══════════════════════════════════════════════════════════════╝

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../components/Toast.jsx';
import { Btn, Card, Field, Input } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';
import { fmtAR } from '../lib/format.js';

const TABS = [
  { id: 'payroll', l: 'الرواتب', i: 'wallet', roles: ['admin', 'acc'] },
  { id: 'attendance', l: 'قواعد الحضور', i: 'fingerprint', roles: ['admin', 'acc'] },
  { id: 'production', l: 'لوائح الإنتاج', i: 'factory', roles: ['admin', 'eng', 'acc'] },
  { id: 'company', l: 'بيانات الشركة', i: 'building', roles: ['admin'] },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const role = user?.role || 'admin';
  const visible = TABS.filter((t) => t.roles.includes(role));
  const [tab, setTab] = useState(visible[0]?.id);

  useEffect(() => {
    if (!visible.find((t) => t.id === tab)) setTab(visible[0]?.id);
  }, [role, tab, visible]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 fade-up">
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
          الإعدادات
        </div>
        <h1 className="text-2xl md:text-3xl font-black mt-1">إعدادات النظام</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        {/* Side tabs (mobile: horizontal scroll, desktop: sidebar) */}
        <Card className="p-3 h-fit">
          <ul className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
            {visible.map((t) => (
              <li key={t.id} className="shrink-0 lg:shrink">
                <button
                  onClick={() => setTab(t.id)}
                  className={`w-full flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-semibold transition whitespace-nowrap ${
                    tab === t.id
                      ? 'bg-brand-500 text-ink-950'
                      : 'hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-600 dark:text-ink-200'
                  }`}
                >
                  <Icon name={t.i} size={15} />
                  <span>{t.l}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <div className="space-y-4">
          {tab === 'production' && <ProductionSettings />}
          {tab === 'payroll' && <PayrollSettings />}
          {tab === 'attendance' && <AttendanceSettings />}
          {tab === 'company' && <CompanySettings />}
        </div>
      </div>
    </div>
  );
}

// ─── Production Settings ───
function ProductionSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings').then((r) => r.data),
  });

  const [bonusAmount, setBonusAmount] = useState('');
  const [penaltyAmount, setPenaltyAmount] = useState('');

  useEffect(() => {
    if (data?.settings?.production) {
      setBonusAmount(String(data.settings.production.bonusAmount ?? 300));
      setPenaltyAmount(String(data.settings.production.penaltyAmount ?? 300));
    }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: async () => {
      await api.put('/settings', {
        key: 'production.bonusAmount',
        value: Number(bonusAmount),
      });
      await api.put('/settings', {
        key: 'production.penaltyAmount',
        value: Number(penaltyAmount),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      toast.push({ tone: 'success', title: 'تم حفظ لوائح الإنتاج' });
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل الحفظ', body: getErrorMessage(err) }),
  });

  if (isLoading) return <Card className="p-6">جاري التحميل…</Card>;

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h2 className="text-lg font-bold">لوائح الإنتاج والحوافز</h2>
        <p className="text-xs text-ink-400 mt-1">
          قيم المكافآت والخصومات الشهرية لعمال الإنتاج. تُحتسب مرة واحدة في الشهر:
          لو إجمالى إنتاج العامل خلال الشهر حقّق هدفه الشهرى ياخد المكافأة، وإلا يتخصم منه. تؤثر على كشوف الرواتب فوراً.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-200 dark:ring-emerald-800 text-center">
          <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-bold uppercase mb-1">
            إذا حقّق الهدف الشهرى
          </div>
          <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 tabular">
            +{fmtAR(bonusAmount)}
          </div>
          <div className="text-xs text-ink-400 mt-1">جنيه / شهر</div>
        </div>
        <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/30 ring-1 ring-rose-200 dark:ring-rose-800 text-center">
          <div className="text-[11px] text-rose-600 dark:text-rose-400 font-bold uppercase mb-1">
            إذا لم يحقق الهدف الشهرى
          </div>
          <div className="text-3xl font-black text-rose-600 dark:text-rose-400 tabular">
            −{fmtAR(penaltyAmount)}
          </div>
          <div className="text-xs text-ink-400 mt-1">جنيه / شهر</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="قيمة مكافأة تحقيق الهدف (جنيه/شهر)">
          <Input type="number" value={bonusAmount} onChange={(e) => setBonusAmount(e.target.value)} />
        </Field>
        <Field label="قيمة خصم عدم تحقيق الهدف (جنيه/شهر)">
          <Input type="number" value={penaltyAmount} onChange={(e) => setPenaltyAmount(e.target.value)} />
        </Field>
      </div>

      <div className="flex justify-end">
        <Btn variant="primary" icon="check" loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
          حفظ التغييرات
        </Btn>
      </div>
    </Card>
  );
}

// ─── Payroll Settings ───
function PayrollSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings').then((r) => r.data),
  });

  const [insuranceRate, setInsuranceRate] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [maxAdvanceRatio, setMaxAdvanceRatio] = useState('');

  useEffect(() => {
    if (data?.settings?.payroll) {
      setInsuranceRate(((data.settings.payroll.insuranceRate ?? 0.11) * 100).toFixed(0));
      setTaxRate(((data.settings.payroll.taxRate ?? 0.05) * 100).toFixed(0));
      setMaxAdvanceRatio(((data.settings.payroll.maxAdvanceRatio ?? 0.5) * 100).toFixed(0));
    }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: async () => {
      await api.put('/settings', { key: 'payroll.insuranceRate', value: Number(insuranceRate) / 100 });
      await api.put('/settings', { key: 'payroll.taxRate', value: Number(taxRate) / 100 });
      await api.put('/settings', { key: 'payroll.maxAdvanceRatio', value: Number(maxAdvanceRatio) / 100 });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      toast.push({ tone: 'success', title: 'تم حفظ إعدادات الرواتب' });
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل الحفظ', body: getErrorMessage(err) }),
  });

  if (isLoading) return <Card className="p-6">جاري التحميل…</Card>;

  return (
    <Card className="p-6 space-y-5">
      <h2 className="text-lg font-bold">إعدادات الرواتب</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="نسبة التأمين الاجتماعي (%)">
          <Input type="number" value={insuranceRate} onChange={(e) => setInsuranceRate(e.target.value)} />
        </Field>
        <Field label="ضريبة كسب العمل (%)">
          <Input type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
        </Field>
        <Field label="الحد الأقصى للسلفة (% من الراتب)">
          <Input type="number" value={maxAdvanceRatio} onChange={(e) => setMaxAdvanceRatio(e.target.value)} />
        </Field>
      </div>
      <div className="flex justify-end">
        <Btn variant="primary" icon="check" loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
          حفظ
        </Btn>
      </div>
    </Card>
  );
}

// ─── Attendance Settings ───
function AttendanceSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings').then((r) => r.data),
  });

  const [graceMinutes, setGraceMinutes] = useState('');
  const [absenceAfterMinutes, setAbsenceAfterMinutes] = useState('');
  const [breakMinutes, setBreakMinutes] = useState('');

  useEffect(() => {
    if (data?.settings?.attendance) {
      setGraceMinutes(String(data.settings.attendance.graceMinutes ?? 10));
      setAbsenceAfterMinutes(String(data.settings.attendance.absenceAfterMinutes ?? 120));
      setBreakMinutes(String(data.settings.attendance.breakMinutes ?? 60));
    }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: async () => {
      await api.put('/settings', { key: 'attendance.graceMinutes', value: Number(graceMinutes) });
      await api.put('/settings', { key: 'attendance.absenceAfterMinutes', value: Number(absenceAfterMinutes) });
      await api.put('/settings', { key: 'attendance.breakMinutes', value: Number(breakMinutes) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      toast.push({ tone: 'success', title: 'تم الحفظ' });
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل الحفظ', body: getErrorMessage(err) }),
  });

  if (isLoading) return <Card className="p-6">جاري التحميل…</Card>;

  return (
    <Card className="p-6 space-y-5">
      <h2 className="text-lg font-bold">قواعد الحضور</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="فترة السماح (دقيقة)">
          <Input type="number" value={graceMinutes} onChange={(e) => setGraceMinutes(e.target.value)} />
        </Field>
        <Field label="غياب يوم كامل بعد (دقيقة تأخير)">
          <Input
            type="number"
            value={absenceAfterMinutes}
            onChange={(e) => setAbsenceAfterMinutes(e.target.value)}
          />
        </Field>
        <Field label="فترة الراحة (دقيقة)">
          <Input type="number" value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} />
        </Field>
      </div>
      <div className="flex justify-end">
        <Btn variant="primary" icon="check" loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
          حفظ
        </Btn>
      </div>
    </Card>
  );
}

// ─── Company Settings (admin) ───
function CompanySettings() {
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings').then((r) => r.data),
  });

  if (isLoading) return <Card className="p-6">جاري التحميل…</Card>;

  const c = data?.settings?.company ?? {};
  return (
    <Card className="p-6 space-y-5">
      <h2 className="text-lg font-bold">بيانات الشركة</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="الاسم بالعربية">
          <Input defaultValue={c.name_ar ?? ''} />
        </Field>
        <Field label="الاسم بالإنجليزية">
          <Input defaultValue={c.name_en ?? ''} dir="ltr" />
        </Field>
        <Field label="السجل التجاري">
          <Input defaultValue={c.cr ?? ''} />
        </Field>
        <Field label="البطاقة الضريبية">
          <Input defaultValue={c.tax ?? ''} />
        </Field>
        <Field label="المقر الرئيسي">
          <Input defaultValue={c.hq_ar ?? ''} />
        </Field>
        <Field label="البريد الرسمي">
          <Input defaultValue={c.email ?? ''} dir="ltr" />
        </Field>
      </div>
    </Card>
  );
}

